import * as React from "react";
import { Box, Stack, TextField, TextFieldProps, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { GridColDef, GridColumnGroupingModel, GridRowsProp } from "@mui/x-data-grid-premium";
import CclGenericAccordian from "../../../../../components/common/cclGenericAccordian";
import CclLoadingButton from "../../../../../components/common/cclButtons/cclLoadingButton";
import ReportsDataGridDataGridPanel from "./reportDataGrid";

export interface OfcUsageReviewProps {
    sessionId: string;
}

const OfcUsageReview: React.FC<OfcUsageReviewProps> = (props) => {
    const sessionId = props.sessionId;
    const [payPeriodEndDate, setPayPeriodEndDate] = React.useState<Date | null>(null);
    const [showData, setShowData] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    const timeout = (delay: number) => new Promise(res => setTimeout(res, delay));

    const generateReport = async () => {
        setShowData(false);
        setLoading(true);
        await timeout(500);
        setShowData(true);
        setLoading(false);
    }

    const onDateChange = (date: Date | null) => {
        setPayPeriodEndDate(date);
    }

    const searchInputSx = {
        "& .MuiOutlinedInput-input": {
            paddingTop: "8px!important",
            paddingBottom: "8px!important",
        },
        "& .MuiInputLabel-root": {
            marginTop: "-8px!important",
        },
        "& .MuiInputLabel-shrink": {
            marginTop: "auto!important",
        },
        width: .25
    };

    const ofcColumns: GridColDef[] = [
        { field: "cos", headerName: "COS", width: 170 },
        { field: "travelFee", headerName: "Fee", width: 120 },
        { field: "travelHours", headerName: "Hours", width: 120 },
        { field: "deliveryFee", headerName: "Fee", width: 120 },
        { field: "deliveryHours", headerName: "Hours", width: 120 },
        { field: "capDevFee", headerName: "Fee", width: 120 },
        { field: "capDevHours", headerName: "Hours", width: 120 },
        { field: "bdCrmFee", headerName: "Fee", width: 120 },
        { field: "bdCrmHours", headerName: "Hours", width: 120 },
        { field: "designFee", headerName: "Fee", width: 120 },
        { field: "designHours", headerName: "Hours", width: 120 },
        { field: "otherFee", headerName: "Fee", width: 120 },
        { field: "otherHours", headerName: "Hours", width: 120 },
        { field: "totalsFee", headerName: "Fee", width: 120 },
        {
            field: "totalsHours",
            headerName: "Hours",
            width: 120,
            renderCell: (params) => {
                const value = Number(params.value);
                let backgroundColor = "inherit";
                if (value > 900) {
                    backgroundColor = "#ffcdd2";
                } else if (value > 700) {
                    backgroundColor = "#fff9c4";
                }
                return (
                    <span style={{ backgroundColor, padding: "4px 8px", borderRadius: "4px" }}>
                        {params.value}
                    </span>
                );
            },
        },
    ];

    const gsoColumns: GridColDef[] = [
        { field: "gso", headerName: "GSO", width: 170 },
        { field: "travelFee", headerName: "Fee", width: 120 },
        { field: "travelHours", headerName: "Hours", width: 120 },
        { field: "deliveryFee", headerName: "Fee", width: 120 },
        { field: "deliveryHours", headerName: "Hours", width: 120 },
        { field: "capDevFee", headerName: "Fee", width: 120 },
        { field: "capDevHours", headerName: "Hours", width: 120 },
        { field: "bdCrmFee", headerName: "Fee", width: 120 },
        { field: "bdCrmHours", headerName: "Hours", width: 120 },
        { field: "designFee", headerName: "Fee", width: 120 },
        { field: "designHours", headerName: "Hours", width: 120 },
        { field: "otherFee", headerName: "Fee", width: 120 },
        { field: "otherHours", headerName: "Hours", width: 120 },
        { field: "totalsFee", headerName: "Fee", width: 120 },
        {
            field: "totalsHours",
            headerName: "Hours",
            width: 120,
            renderCell: (params) => {
                const value = Number(params.value);
                let backgroundColor = "inherit";
                if (value > 900) {
                    backgroundColor = "#ffcdd2";
                } else if (value > 700) {
                    backgroundColor = "#fff9c4";
                }
                return (
                    <span style={{ backgroundColor, padding: "4px 8px", borderRadius: "4px" }}>
                        {params.value}
                    </span>
                );
            },
        },
    ];

    const columnGroupingModel: GridColumnGroupingModel = [
        {
            groupId: "travel",
            headerName: "6002- Travel",
            children: [{ field: "travelFee" }, { field: "travelHours" }],
        },
        {
            groupId: "delivery",
            headerName: "6005 - Delivery",
            children: [{ field: "deliveryFee" }, { field: "deliveryHours" }],
        },
        {
            groupId: "capDev",
            headerName: "6006 - CapDev",
            children: [{ field: "capDevFee" }, { field: "capDevHours" }],
        },
        {
            groupId: "bdCrm",
            headerName: "6008 - BD/CRM",
            children: [{ field: "bdCrmFee" }, { field: "bdCrmHours" }],
        },
        {
            groupId: "design",
            headerName: "6009 - Design",
            children: [{ field: "designFee" }, { field: "designHours" }],
        },
        {
            groupId: "other",
            headerName: "6000, 6001 - Other",
            children: [{ field: "otherFee" }, { field: "otherHours" }],
        },
        {
            groupId: "totals",
            headerName: "Totals",
            children: [{ field: "totalsFee" }, { field: "totalsHours" }],
        },
    ];

    const data: GridRowsProp = [
        {
            id: 1,
            cos: "Sarah Johnson",
            gso: "Michael Chen",
            travelFee: 1200, travelHours: 8,
            deliveryFee: 2500, deliveryHours: 15,
            capDevFee: 1800, capDevHours: 10,
            bdCrmFee: 900, bdCrmHours: 5,
            designFee: 1500, designHours: 9,
            otherFee: 600, otherHours: 3,
            totalsFee: 8500, totalsHours: 50,
        },
        {
            id: 2,
            cos: "Emily Rodriguez",
            gso: "James Wilson",
            travelFee: 1500, travelHours: 10,
            deliveryFee: 2000, deliveryHours: 12,
            capDevFee: 2200, capDevHours: 12,
            bdCrmFee: 1100, bdCrmHours: 6,
            designFee: 1800, designHours: 10,
            otherFee: 800, otherHours: 4,
            totalsFee: 9400, totalsHours: 950,
        },
        {
            id: 3,
            cos: "David Martinez",
            gso: "Jessica Brown",
            travelFee: 900, travelHours: 6,
            deliveryFee: 3000, deliveryHours: 18,
            capDevFee: 1500, capDevHours: 8,
            bdCrmFee: 800, bdCrmHours: 4,
            designFee: 1200, designHours: 7,
            otherFee: 500, otherHours: 2,
            totalsFee: 7900, totalsHours: 45,
        },
        {
            id: 4,
            cos: "Amanda Taylor",
            gso: "Christopher Lee",
            travelFee: 1300, travelHours: 9,
            deliveryFee: 2200, deliveryHours: 13,
            capDevFee: 2000, capDevHours: 11,
            bdCrmFee: 950, bdCrmHours: 5,
            designFee: 1600, designHours: 9,
            otherFee: 700, otherHours: 3,
            totalsFee: 8750, totalsHours: 750,
        },
        {
            id: 5,
            cos: "Robert Anderson",
            gso: "Nicole Garcia",
            travelFee: 1100, travelHours: 7,
            deliveryFee: 2600, deliveryHours: 16,
            capDevFee: 1900, capDevHours: 10,
            bdCrmFee: 850, bdCrmHours: 4,
            designFee: 1400, designHours: 8,
            otherFee: 600, otherHours: 3,
            totalsFee: 8450, totalsHours: 48,
        },
    ];

    return (
        <Box sx={{ width: "100%" }}>
            <CclGenericAccordian
                headingText={"OFC Usage Report"}
                defaultExpanded
            >
                <Stack sx={{margin: 1, padding: 1 }} spacing={2}>
                    <Stack direction={"row"} spacing={5} sx={{ mx: 2, mt: 2 }}>
                        <Typography sx={{ alignSelf: "center"}} variant="h6">Pay Period End Date</Typography>
                        <DatePicker
                            onChange={onDateChange}
                            value={payPeriodEndDate}
                            renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => {
                                params.fullWidth = false;
                                return (
                                    <TextField
                                        {...params}
                                        sx={searchInputSx}
                                    />
                                );
                            }}
                        />
                        <CclLoadingButton
                            mode={"primary"}
                            sx={{marginLeft: "auto"}}
                            onClick={generateReport}
                            loading={loading}
                        >
                            Generate
                        </CclLoadingButton>
                    </Stack>
                    {showData && (
                        <Stack spacing={3}>
                            <Typography variant="h6">COS</Typography>
                            <ReportsDataGridDataGridPanel
                                sessionId={sessionId}
                                columns={ofcColumns}
                                columnGroupingModel={columnGroupingModel}
                                data={data}
                            />
                            <Typography variant="h6">GSO</Typography>
                            <ReportsDataGridDataGridPanel
                                sessionId={sessionId}
                                columns={gsoColumns}
                                columnGroupingModel={columnGroupingModel}
                                data={data}
                            />
                        </Stack>
                    )}
                </Stack>
            </CclGenericAccordian>
        </Box>
    );
};

export default OfcUsageReview;
